// @flow
import classnames from 'classnames';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import CloseIcon from '@material-ui/icons/Close';

import ExpandIcon from '../../../../assets/icons/expand.svg';
import KeyboardArrowDown from '../../../../assets/icons/keyboard-arrow-down.svg';

import InviteCard from '../../../components/Cards/InviteCard';
import LoadingText from '../../../components/LoadingText';
import ParticipantList from '../../../components/ParticipantList';
import SnackbarWithCountdown from '../../../components/SnackbarWithCountdown';
import WebsocketReconnectingBanner from '../../../components/Network/WebsocketReconnectingBanner';

import AudioRecorder from '../../../modules/AudioRecorder';
import ScribeKeypressHandlerContainer from '../../../modules/scribe/ScribeKeypressHandlerContainer';
import ScribeTranscriptListContainer from '../../../modules/scribe/ScribeTranscriptListContainer';
import ScribeVolumeController from '../../../modules/scribe/ScribeVolumeController';

import KeyboardInput from '../KeyboardInput';
import NotificationContainer from '../NotificationContainer';

import type { Notification } from '../../../types/cc-mode';
import type { JoiningStatus } from '../../../types/conversation';
import type { Displayable } from '../../../types/displayable';
import { isDefaultRoomId } from '../../../utils/ws-v1';

import useStyles from './ConversationView.styles';
import AddParticipantButton from '../../../components/CCMode/AddParticipantButton';
import { ipcRendererWithDeregistering } from '../../../utils/electron';

interface Props {
  addNotification: (notification: Notification) => void;
  appSubmittedTypeform: Object;
  clearNotifications: () => void;
  displayable: Displayable;
  displayShortcuts: boolean;
  features: Object;
  fontSize: number;
  isInConversation: boolean;
  isInviteCardOpen: boolean;
  joiningStatus: JoiningStatus;
  muted: boolean;
  onEndConversation: () => void;
  roomEndingWarningCountdown: number;
  roomEndingWarningBegin: number;
  roomEndingWarningProlongUrl: string;
  scribe: Object;
  setInviteCardOpen: (isOpen: boolean) => void;
  setJoiningStatus: (joiningStatus: JoiningStatus) => void;
  setShowBubbleAboveCCMode: (shouldShow: boolean) => void;
  showBubbleAboveCCMode: boolean;
  socketConnectionStatus: string;
  speakerListened: boolean;
  speakers: Object;
  status: Object;
  updateElectronCaptionMode: (isElectronCaptionMode: boolean) => void;
  user: Object;
}

const ConversationView = ({
  addNotification,
  appSubmittedTypeform,
  clearNotifications,
  displayable,
  displayShortcuts,
  features,
  fontSize,
  isInConversation,
  isInviteCardOpen,
  joiningStatus,
  muted,
  onEndConversation,
  roomEndingWarningCountdown,
  roomEndingWarningBegin,
  roomEndingWarningProlongUrl,
  scribe,
  setInviteCardOpen,
  setJoiningStatus,
  setShowBubbleAboveCCMode,
  showBubbleAboveCCMode,
  socketConnectionStatus,
  speakerListened,
  speakers,
  status,
  updateElectronCaptionMode,
  user,
}: Props) => {
  const classes = useStyles({ showBubbleAboveCCMode, fontSize });
  const theme = useTheme();
  const { t } = useTranslation();

  const [keyboardInputOpen, setKeyboardInputOpen] = useState(false);
  const keyboardInputComponent = useRef(null);

  const showAudioControlsAndKeyboard = displayable.audio && !scribe && !muted && isInConversation;

  useEffect(() => {
    const setCCWindowDimensionsInLocalStorage = (event: any) => {
      const { args } = event;
      let [width, height] = [0, 0];
      if (args.length >= 2) [width, height] = args;
      if (keyboardInputComponent && keyboardInputComponent.current) {
        height -= keyboardInputComponent.current.clientHeight;
      }
      localStorage.setItem('ccModeWidth', JSON.stringify(width));
      localStorage.setItem('ccModeHeight', JSON.stringify(height));
    };
    const setCCWindowPositionInLocalStorage = (event: any) => {
      const { args } = event;
      if (args.length < 2) return;
      const [x, y] = args;
      localStorage.setItem('ccModePositionX', JSON.stringify(x));
      localStorage.setItem('ccModePositionY', JSON.stringify(y));
    };

    if (window.isElectron) {
      ipcRendererWithDeregistering.addEventListener('window-resized', setCCWindowDimensionsInLocalStorage);
      ipcRendererWithDeregistering.addEventListener('window-moved', setCCWindowPositionInLocalStorage);
    }

    return () => {
      if (window.isElectron) {
        ipcRendererWithDeregistering.removeEventListener('window-resized', setCCWindowDimensionsInLocalStorage);
        ipcRendererWithDeregistering.removeEventListener('window-moved', setCCWindowPositionInLocalStorage);
      }
      clearNotifications();
      setShowBubbleAboveCCMode(false);
      setInviteCardOpen(false);
      setJoiningStatus('none');
    };
  }, []);

  useEffect(() => {
    if (joiningStatus === 'accepted') {
      addNotification({ text: t('conversation.inviteCard.joinedAvaRoom'), timestamp: Date.now(), type: 'success' });
    }
  }, [joiningStatus]);

  return (
    <div className={classes.root}>
      <section
        className={classnames(classes.topContainer, {
          [classes.visible]: showBubbleAboveCCMode,
        })}
      >
        <div className={classes.participants}>
          {status?.id && user && !isDefaultRoomId(status.id) && (
            <ParticipantList
              scribe={features.scribe && scribe}
              theme={theme}
              speakers={speakers}
              roomSpeakers={status.speakers}
              participants={status.participants || []}
              transcriptOwnersId={status.transcriptsOwners || []}
              status={status}
              displayShortcuts={displayShortcuts}
              speakerListened={speakerListened}
              user={user}
            />
          )}
          <AddParticipantButton isActive={isInviteCardOpen} onClick={() => setInviteCardOpen(!isInviteCardOpen)} />
        </div>
        <div className={classes.conversationButtons}>
          <Tooltip title={t('ccMode.buttons.fullScreen')} placement="bottom">
            <div>
              <button className={classes.conversationButton} onClick={() => updateElectronCaptionMode(false)}>
                <img src={ExpandIcon} alt="" />
              </button>
            </div>
          </Tooltip>
          <Tooltip title={t('ccMode.buttons.closeRoom')} placement="bottom">
            <div>
              <button
                className={classnames(classes.conversationButton, classes.closeButton)}
                onClick={onEndConversation}
              >
                <CloseIcon className={classes.closeIcon} />
              </button>
            </div>
          </Tooltip>
        </div>
      </section>
      <section
        className={classnames(classes.conversationContainer, {
          [classes.conversationContainerKeyboardOpen]: keyboardInputOpen,
          [classes.readOnlyConversation]: !showAudioControlsAndKeyboard,
        })}
        onMouseEnter={() => setShowBubbleAboveCCMode(true)}
      >
        <div className={classes.conversationDraggableContainer}>
          <WebsocketReconnectingBanner />
          <NotificationContainer />
          <SnackbarWithCountdown
            classes={{
              anchorOriginTopLeft: classes.snackbarCountdown,
            }}
            message={t('snackbar.convoRestrictedSessionWarning')}
            countdown={roomEndingWarningCountdown}
            countdownBegin={roomEndingWarningBegin}
            additionalActionMessage={t('snackbar.convoRestrictedSessionProlong')}
            additionalActionUrl={roomEndingWarningProlongUrl}
            appSubmittedTypeform={appSubmittedTypeform}
          />
          <span>{socketConnectionStatus === 'CONNECTED' && !muted && <ScribeKeypressHandlerContainer />}</span>
          {!isInConversation || joiningStatus === 'requested' ? (
            <div className={classes.loadingTextContainer}>
              <LoadingText className={classes.loadingText}>
                {joiningStatus === 'requested'
                  ? t('ccMode.conversation.joiningAvaRoom')
                  : t('ccMode.conversation.startingAvaRoom')}
              </LoadingText>
            </div>
          ) : (
            <>
              {showAudioControlsAndKeyboard && (
                <div className={classes.controlsContainer}>
                  <ScribeVolumeController className={classes.volumeController} electronCaptionMode />
                  <AudioRecorder className={classes.audioRecorder} showExpandedMic={showBubbleAboveCCMode} />
                  <div style={{ height: 32 }}>
                    {!keyboardInputOpen && (
                      <Tooltip
                        classes={{ tooltipPlacementTop: classes.tooltipPlacementTop }}
                        title={t('ccMode.tooltip.keyboard')}
                        placement="top"
                      >
                        <button className={classes.openKeyboardInputButton}>
                          <img
                            src={KeyboardArrowDown}
                            alt={t('ccMode.keyboard.keyboardArrowDownButton')}
                            onClick={() => {
                              window.electronIPC.sendCCMode('keyboard', true);
                              setKeyboardInputOpen(true);
                            }}
                          />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              )}
              <div className={classes.transcriptsContainer}>
                {isInviteCardOpen && (
                  <div className={classes.inviteCardContainer}>
                    <InviteCard />
                  </div>
                )}
                <ScribeTranscriptListContainer />
              </div>
            </>
          )}
        </div>
      </section>
      <section
        ref={keyboardInputComponent}
        className={classnames(classes.bottomContainer, {
          [classes.keyboardInputOpen]: keyboardInputOpen,
        })}
        onMouseEnter={() => setShowBubbleAboveCCMode(true)}
      >
        {keyboardInputOpen && (
          <KeyboardInput
            onCloseKeyboard={() => {
              setKeyboardInputOpen(false);
              window.electronIPC.sendCCMode('keyboard', false);
            }}
          />
        )}
      </section>
    </div>
  );
};

export default ConversationView;
